/**
 * Sticky Add to Cart functionality
 * Handles scroll detection, variant selection, and add to cart actions
 */

class StickyAddToCart {
  constructor() {
    this.stickyElement = document.querySelector('.sticky-add-to-cart');
    if (!this.stickyElement) return;
    this.mediaQuery = window.matchMedia('(max-width: 767px)');
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.isMobileActive = false;
    this.hasEarlyAccessSignUp = this.stickyElement.dataset.hasEarlyAccessSignUp === 'true';

    // Run once immediately
    this.handleViewportChange(this.mediaQuery);

    // React to viewport changes
    this.mediaQuery.addEventListener('change', this.handleViewportChange);
  }

  handleViewportChange(e) {
    const matches = e.matches;
    if (matches && !this.isMobileActive && !this.hasEarlyAccessSignUp) {
      this.init();
      this.isMobileActive = true;
    } else if (!matches && this.isMobileActive) {
      this.destroy();
      this.isMobileActive = false;
    }
  }

  init() {
    this.sectionId = this.stickyElement.dataset.sectionId;
    this.productId = this.stickyElement.dataset.productId;
    this.selectASize = this.stickyElement.dataset.selectASize === 'true';
    this.buttonText = this.stickyElement.dataset.buttonText;
    this.selectSizeText = this.stickyElement.dataset.selectSizeText;
    this.defaultSizeSet = false;

    this.stickyButton = this.stickyElement.querySelector('.sticky-add-to-cart__button');
    this.stickyButtonText = this.stickyElement.querySelector('.sticky-add-to-cart__button-text');
    this.priceElement = this.stickyElement.querySelector('.sticky-add-to-cart__price');
    this.productImage = this.stickyElement.querySelector('.sticky-add-to-cart__product-image');
    this.stickyVariantSelector = this.stickyElement.querySelector('.sticky-add-to-cart__variant-selector');


    // Find the original buy buttons and product form
    this.originalBuyButtons = document.querySelector(`[data-section="${this.sectionId}"] .product-form__buttons`);
    this.productForm = document.querySelector(`#product-form-${this.sectionId}`);
    this.productFormVariantId = this.productForm ? this.productForm.querySelector('input[name="id"]').value : null;
    this.originalAddToCartButton = document.querySelector(`#ProductSubmitButton-${this.sectionId}`);
    this.variantPicker = document.querySelector(`#variant-selects-${this.sectionId}`) || document.querySelector(`[data-section="${this.sectionId}"] .product-form__input`);

    if (!this.productForm || !this.originalAddToCartButton) {
      console.warn('Sticky Add to Cart: Required elements not found');
      return;
    }

    this.bindEvents();
    this.setupScrollObserver();
  }

  bindEvents() {
    // Handle sticky button click
    this.stickyButton.addEventListener('click', this.handleStickyButtonClick.bind(this));

    // Listen for variant changes from the main product form
    this.productForm.addEventListener('change', this.handleVariantChange.bind(this));

    // Listen for variant updates from product-info.js
    document.addEventListener('variant:change', this.handleVariantUpdate.bind(this));

    this.stickyVariantSelector?.addEventListener('change', this.handleStickyVariantSelector.bind(this));
  }

  setupScrollObserver() {
    if (!this.originalBuyButtons) return;

    // Use Intersection Observer to detect when original buy buttons go out of view
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.hideStickyButton();
          } else {
            this.showStickyButton();
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '-70px 0px -70px 0px'
      }
    );

    this.observer.observe(this.originalBuyButtons);
  }

  showStickyButton() {
    this.updateButtonState();
    this.stickyElement.classList.add('is-visible');
  }

  hideStickyButton() {
    this.stickyElement.classList.remove('is-visible');
  }

  handleStickyButtonClick(event) {
    event.preventDefault();

    // If no variant is selected and we need size selection, scroll to variant picker
    if (this.needsVariantSelection()) {
      this.scrollToVariantPicker();
      return;
    }

    // Otherwise, trigger the original add to cart button
    this.triggerAddToCart();
  }

  needsVariantSelection() {
    if (!this.selectASize) return false;

    const formData = new FormData(this.productForm);
    const variantId = formData.get('id');

    // Check if no variant is selected or if variant picker has no selection
    return !variantId || variantId === '';
  }

  scrollToVariantPicker() {
    if (this.variantPicker) {
      this.variantPicker.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      // Add a subtle flash effect to draw attention
      this.variantPicker.style.animation = 'flash-highlight 1s ease-in-out';
      setTimeout(() => {
        this.variantPicker.style.animation = '';
      }, 1000);
    }
  }

  triggerAddToCart() {
    // Show loading state
    this.setLoadingState(true);

    // Create a synthetic click event on the original button
    if (this.originalAddToCartButton) {
      this.originalAddToCartButton.click();
    }

    // Reset loading state after a delay (the original button will handle the actual cart addition)
    setTimeout(() => {
      this.setLoadingState(false);
    }, 2000);
  }

  setLoadingState(loading) {
    const spinner = this.stickyButton.querySelector('.loading__spinner');

    if (loading) {
      this.stickyButton.classList.add('loading');
      this.stickyButton.disabled = true;
      if (spinner) spinner.classList.remove('hidden');
    } else {
      this.stickyButton.classList.remove('loading');
      this.stickyButton.disabled = false;
      if (spinner) spinner.classList.add('hidden');
    }
  }

  handleVariantChange(event) {
    // Debounce rapid changes
    clearTimeout(this.variantChangeTimeout);
    this.variantChangeTimeout = setTimeout(() => {
      if (event.target.value === this.productFormVariantId) return;
      this.productFormVariantId = event.target.value;
      this.updateButtonState();
    }, 100);
  }

  handleVariantUpdate(event) {
    if (event.detail && event.detail.sectionId === this.sectionId) {
      this.updateFromVariantData(event.detail);
    }
  }

  handleCartUpdate(event) {
    // Reset loading state when cart updates
    this.setLoadingState(false);
  }

  handleStickyVariantSelector(event) {
    const selectedVariantID = event.target.value;
    const variantSelects = document.querySelector(`variant-selects[data-section='${this.sectionId}'] fieldset`);

    // Guard against null variantSelects
    if (!variantSelects) {
      console.warn(`Sticky Add to Cart: variant-selects element not found for section ${this.sectionId}`);
      return;
    }

    const targetInput = variantSelects.querySelector(
      `[data-option-variant-id="${selectedVariantID}"]`
    );

    if (targetInput) {
      // Change the input’s state
      targetInput.checked = true;

      // Fire a real change event so all listeners run
      targetInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  updateButtonStateWithSelectASize() {
    const formData = new FormData(this.productForm);
    const variantId = formData.get('id');

    // Update button text based on variant selection
    if (this.needsVariantSelection()) {
      this.stickyButtonText.textContent = this.selectSizeText;
      this.stickyButton.classList.add('select-variant');
    } else {
      this.stickyButtonText.textContent = this.buttonText;
      this.stickyButton.classList.remove('select-variant');
    }

    this.updateStickyUi(variantId);
  }

  updateButtonStateWithoutSelectASize() {
    // on load, no variant is selected in product form, set it to that of sticky add to cart
    if (!this.productFormVariantId) {
      this.updateProductFormVariantSelectsVariant();
    }
    else {
      this.updateStickyAddToCartSelectsVariant()
    }
  }

  updateStickyUi(variantId) {
    // Update price if available
    this.updatePriceDisplay();

    // Update product image if variant has a featured image
    this.updateProductImage(variantId);

    // match button state to that of original add to cart button, only need to update text when product form variantId is set
    if (this.productFormVariantId) {
      this.stickyButton.disabled = this.originalAddToCartButton.disabled;
      this.stickyButtonText.textContent = this.originalAddToCartButton.textContent;
    }
  }

  // If product forms variant-selects has changed, need to update sticky add to cart select
  updateStickyAddToCartSelectsVariant() {
    // Guard against null stickyVariantSelector (when select_a_size_before_add_to_cart is true)
    if (!this.stickyVariantSelector) {
      const selectedVariantID = this.productFormVariantId;
      this.updateStickyUi(selectedVariantID);
      return;
    }

    const selectedVariantID = this.productFormVariantId;
    const targetOption = this.stickyVariantSelector.querySelector(`option[value='${selectedVariantID}']`);
    if (targetOption) {
      this.stickyVariantSelector.value = targetOption.value;
    }

    this.updateStickyUi(selectedVariantID);
  }

  // set set product form variant selects to that of sticky add to cart
  updateProductFormVariantSelectsVariant() {

    const variantSelects = document.querySelector(`variant-selects[data-section='${this.sectionId}'] fieldset`);

    // Guard against null variantSelects
    if (!variantSelects) {
      console.error(`Sticky Add to Cart: variant-selects element not found for section ${this.sectionId}`);
      return;
    }

    // Guard against null stickyVariantSelector (when select_a_size_before_add_to_cart is true)
    if (!this.stickyVariantSelector) {
      // Just update the UI with the product form's variant ID
      const selectedVariantID = this.productFormVariantId;
      this.updateStickyUi(selectedVariantID);
      return;
    }

    //if firstLoad set the varianID to size 10 if available
    if (!this.defaultSizeSet) {
      const sizeTenOption = this.stickyVariantSelector.querySelector("option[data-size='10']");
      if (sizeTenOption && !sizeTenOption.disabled) {
        this.stickyVariantSelector.value = sizeTenOption.value;
      }
      this.defaultSizeSet = true;
    }

    const selectedVariantID = this.stickyVariantSelector.value;
    const targetInput = variantSelects.querySelector(`[data-option-variant-id='${selectedVariantID}']`);

    if (targetInput) {
      // Change the input's state
      targetInput.checked = true;

      // Fire a real change event so all listeners run
      targetInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    this.updateStickyUi(selectedVariantID);
  }

  updateButtonState() {
    if (this.selectASize) {
      this.updateButtonStateWithSelectASize();
    } else {
      this.updateButtonStateWithoutSelectASize();
    }
  }

  updateFromVariantData(variantData) {
    if (variantData.variant) {
      // Update button state based on availability
      if (!variantData.variant.available) {
        this.stickyButtonText.textContent = 'Sold out';
        this.stickyButton.disabled = true;
      } else {
        this.stickyButton.disabled = false;
        this.updateButtonState();
      }

      // Update price
      if (this.priceElement && variantData.price) {
        this.priceElement.innerHTML = variantData.price;
      }

      // Update image
      if (this.productImage && variantData.variant.featured_media) {
        this.updateProductImageFromMedia(variantData.variant.featured_media);
      }
    }
  }

  updatePriceDisplay() {
    // Get price from the main product price element
    const mainPriceElement = document.querySelector(`#price-${this.sectionId}`);
    if (mainPriceElement && this.priceElement) {
      this.priceElement.innerHTML = mainPriceElement.innerHTML;
    }
  }

  updateProductImage(variantId) {
    if (!this.productImage || !variantId) return;

    // Look for variant-specific image
    const variantInput = this.productForm.querySelector(`input[value="${variantId}"]`);
    if (variantInput && variantInput.dataset.variantImage) {
      this.productImage.src = variantInput.dataset.variantImage;
    }
  }

  updateProductImageFromMedia(media) {
    if (!this.productImage || !media) return;

    const imageUrl = media.preview_image?.src || media.src;
    if (imageUrl) {
      // Create a smaller version for the sticky bar
      const sizedImageUrl = imageUrl.replace(/\.(jpg|jpeg|png|webp)/i, '_120x120.$1');
      this.productImage.src = sizedImageUrl;
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.variantChangeTimeout) {
      clearTimeout(this.variantChangeTimeout);
    }
  }
}

// Add flash highlight animation styles if not already present
if (!document.querySelector('#sticky-add-to-cart-styles')) {
  const style = document.createElement('style');
  style.id = 'sticky-add-to-cart-styles';
  style.textContent = `
    @keyframes flash-highlight {
      0%, 100% { background-color: transparent; }
      50% { background-color: rgba(var(--color-button), 0.1); }
    }
  `;
  document.head.appendChild(style);
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StickyAddToCart();
  });
} else {
  new StickyAddToCart();
}

// Also initialize after Shopify section reloads
document.addEventListener('shopify:section:load', () => {
  new StickyAddToCart();
});
